import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { leadApi, clientApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Save, Search, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const SALES_STAGES = ['Lead', 'Qualified', 'Estimate', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const PROJECT_TYPES = [
  'Kitchen Remodel',
  'Bathroom Remodel',
  'Home Addition',
  'Whole House Renovation',
  'Basement Finishing',
  'Deck/Patio',
  'Roofing',
  'Siding',
  'Windows/Doors',
  'New Construction',
  'Small Project',
  'Maintenance',
  'Repair',
  'Other',
];
const LEAD_SOURCES = [
  'Website',
  'Referral',
  'Google',
  'Facebook',
  'Home Advisor',
  'Angi',
  'Houzz',
  'Trade Show',
  'Direct Mail',
  'Other',
];
const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];
const PROJECT_TIMEFRAMES = [
  { value: 'immediately', label: 'Immediately' },
  { value: '0-6-months', label: '0-6 Months' },
  { value: '6-12-months', label: '6-12 Months' },
  { value: 'planning', label: 'Planning Stage' },
];

export default function LeadForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('client_id');
  const isEditing = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing || Boolean(clientIdFromUrl));
  const [lookingUp, setLookingUp] = useState(false);
  const [existingClient, setExistingClient] = useState(null);
  const [prefilledFromClient, setPrefilledFromClient] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    source: '',
    project_type: '',
    project_timeframe: '',
    estimated_value: '',
    notes: '',
    stage: 'Lead',
  });

  useEffect(() => {
    if (isEditing) {
      fetchLead();
    } else if (clientIdFromUrl) {
      fetchClientAndPrefill(clientIdFromUrl);
    } else {
      setFetching(false);
    }
  }, [id, clientIdFromUrl]);

  const fetchClientAndPrefill = async (clientId) => {
    try {
      const response = await clientApi.getOne(clientId);
      const client = response.data;
      
      // Parse name into first_name and last_name
      let firstName = '';
      let lastName = '';
      if (client.name) {
        const nameParts = client.name.trim().split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.slice(1).join(' ') || '';
      } else if (client.first_name || client.last_name) {
        firstName = client.first_name || '';
        lastName = client.last_name || '';
      }

      setFormData({
        first_name: firstName,
        last_name: lastName,
        company: client.company || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        city: client.city || '',
        state: client.state || '',
        zip_code: client.zip || client.zip_code || '',
        source: '',
        project_type: '',
        project_timeframe: '',
        estimated_value: '',
        notes: '',
        stage: 'Lead',
      });
      
      setExistingClient(client);
      setPrefilledFromClient(true);
      toast.success(`Pre-filled with ${client.name || firstName + ' ' + lastName}'s information`);
    } catch (error) {
      console.error('Failed to fetch client:', error);
      toast.error('Could not load client information');
    } finally {
      setFetching(false);
    }
  };

  const fetchLead = async () => {
    try {
      const response = await leadApi.getOne(id);
      setFormData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        company: response.data.company || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        zip_code: response.data.zip_code || '',
        source: response.data.source || '',
        project_type: response.data.project_type || '',
        project_timeframe: response.data.project_timeframe || '',
        estimated_value: response.data.estimated_value?.toString() || '',
        notes: response.data.notes || '',
        stage: response.data.stage || 'Lead',
      });
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/leads');
    } finally {
      setFetching(false);
    }
  };

  // Phone lookup with debounce
  const handlePhoneLookup = useCallback(async (phone) => {
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setExistingClient(null);
      return;
    }
    
    setLookingUp(true);
    try {
      const response = await clientApi.lookupByPhone(phone);
      if (response.data.found) {
        setExistingClient(response.data.client);
      } else {
        setExistingClient(null);
      }
    } catch (error) {
      setExistingClient(null);
    } finally {
      setLookingUp(false);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Trigger phone lookup when phone changes
    if (name === 'phone' && !isEditing) {
      // Debounce the lookup
      clearTimeout(window.phoneLookupTimeout);
      window.phoneLookupTimeout = setTimeout(() => {
        handlePhoneLookup(value);
      }, 500);
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const autofillFromClient = () => {
    if (!existingClient) return;
    
    setFormData({
      ...formData,
      first_name: existingClient.first_name || '',
      last_name: existingClient.last_name || '',
      company: existingClient.company || '',
      email: existingClient.email || '',
      phone: existingClient.phone || '',
      address: existingClient.address || '',
      city: existingClient.city || '',
      state: existingClient.state || '',
      zip_code: existingClient.zip_code || '',
    });
    toast.success('Autofilled from existing client');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.first_name.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.last_name.trim()) {
      toast.error('Last name is required');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone is required');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Address is required');
      return;
    }
    if (!formData.city.trim()) {
      toast.error('City is required');
      return;
    }
    if (!formData.state) {
      toast.error('State is required');
      return;
    }
    if (!formData.zip_code.trim()) {
      toast.error('ZIP code is required');
      return;
    }
    if (!formData.project_type) {
      toast.error('Project type is required');
      return;
    }
    
    setLoading(true);

    // Convert empty strings to null for optional fields
    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      company: formData.company?.trim() || null,
      email: formData.email?.trim() || null,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
      source: formData.source || null,
      project_type: formData.project_type,
      project_timeframe: formData.project_timeframe || null,
      estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
      notes: formData.notes || null,
      stage: formData.stage,
      // Link to existing client if prefilled from one
      client_id: prefilledFromClient && existingClient ? existingClient.id : null,
    };

    try {
      if (isEditing) {
        await leadApi.update(id, payload);
        toast.success('Lead updated');
      } else {
        const response = await leadApi.create(payload);
        toast.success('Lead created');
        navigate(`/leads/${response.data.id}`);
        return;
      }
      navigate(`/leads/${id}`);
    } catch (error) {
      // Handle Pydantic validation errors
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg || e.message).join(', '));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to save lead');
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lead-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(isEditing ? `/leads/${id}` : '/leads')}
          className="rounded-sm"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Edit Lead' : 'New Lead'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing 
              ? 'Update lead information' 
              : prefilledFromClient && existingClient
                ? `Creating lead for ${existingClient.name || formData.first_name + ' ' + formData.last_name}`
                : 'Add a new lead to your pipeline'}
          </p>
        </div>
      </div>

      {/* Show notice when prefilled from existing client */}
      {prefilledFromClient && existingClient && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-6">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-900">Pre-filled from existing client</p>
              <p className="text-sm text-blue-700">
                Contact information has been auto-populated from the client record. You can edit if needed.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="John"
                    className="rounded-sm mt-1"
                    required
                    data-testid="lead-first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Smith"
                    className="rounded-sm mt-1"
                    required
                    data-testid="lead-last-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="rounded-sm mt-1"
                    data-testid="lead-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <div className="relative">
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="rounded-sm mt-1"
                      required
                      data-testid="lead-phone-input"
                    />
                    {lookingUp && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5">
                        <Search className="h-4 w-4 animate-pulse text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  {existingClient && !isEditing && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded-sm border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <UserCheck className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            Existing client: {existingClient.name || `${existingClient.first_name} ${existingClient.last_name}`}
                          </span>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={autofillFromClient}
                          className="h-7 text-xs"
                        >
                          Autofill
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="company">Company <span className="text-muted-foreground text-xs">(if working for a contractor)</span></Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="e.g. Acme Builders"
                    className="rounded-sm mt-1"
                    data-testid="lead-company-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Address</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    className="rounded-sm mt-1"
                    required
                    data-testid="lead-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Austin"
                    className="rounded-sm mt-1"
                    required
                    data-testid="lead-city-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(val) => handleSelectChange('state', val)}
                    >
                      <SelectTrigger className="rounded-sm mt-1" data-testid="lead-state-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="zip_code">ZIP Code *</Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="78701"
                      className="rounded-sm mt-1"
                      required
                      data-testid="lead-zip-input"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Additional notes about this lead..."
                  rows={4}
                  className="rounded-sm"
                  data-testid="lead-notes-input"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Project Details */}
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle className="text-lg">Project Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="project_type">Project Type *</Label>
                  <Select
                    value={formData.project_type}
                    onValueChange={(val) => handleSelectChange('project_type', val)}
                  >
                    <SelectTrigger className="rounded-sm mt-1" data-testid="lead-project-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="project_timeframe">Project Timeframe</Label>
                  <Select
                    value={formData.project_timeframe}
                    onValueChange={(val) => handleSelectChange('project_timeframe', val)}
                  >
                    <SelectTrigger className="rounded-sm mt-1" data-testid="lead-timeframe-select">
                      <SelectValue placeholder="When do they need this?" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_TIMEFRAMES.map((tf) => (
                        <SelectItem key={tf.value} value={tf.value}>
                          {tf.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="estimated_value">Estimated Value ($)</Label>
                  <Input
                    id="estimated_value"
                    name="estimated_value"
                    type="number"
                    value={formData.estimated_value}
                    onChange={handleChange}
                    placeholder="50000"
                    className="rounded-sm mt-1"
                    data-testid="lead-value-input"
                  />
                </div>
                <div>
                  <Label htmlFor="source">Lead Source</Label>
                  <Select
                    value={formData.source}
                    onValueChange={(val) => handleSelectChange('source', val)}
                  >
                    <SelectTrigger className="rounded-sm mt-1" data-testid="lead-source-select">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAD_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isEditing && (
                  <div>
                    <Label htmlFor="stage">Stage</Label>
                    <Select
                      value={formData.stage}
                      onValueChange={(val) => handleSelectChange('stage', val)}
                    >
                      <SelectTrigger className="rounded-sm mt-1" data-testid="lead-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SALES_STAGES.map((stage) => (
                          <SelectItem key={stage} value={stage}>
                            {stage}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm bg-slate-900 hover:bg-slate-800 h-11 btn-press"
              data-testid="save-lead-btn"
            >
              {loading ? (
                <span className="loading-spinner" />
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Lead' : 'Create Lead'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
